/**
 * ConversionWaterfall — W55 "MQL → SQL Conversion Waterfall"
 * W57: rewired to query mql_events via /api/marketing/funnel instead of accounts.status proxy.
 * W66.X14: fixed off-by-one in drop-off attribution and swapped palette to canon.
 *
 * Each row shows what happens to the volume sitting on THAT row: the conversion
 * rate to the next stage and the count lost in that transition. The Opportunity
 * row is terminal and shows no downstream label.
 *
 * Reads:
 *   MQL row         — marketing claims these are qualified. Label: % accepted by
 *                     BDR as SAL, count rejected by BDR.
 *   SAL row         — BDR agreed to work these. Label: % advanced to SQL after
 *                     qualification calls, count dropped in BDR qualification.
 *   SQL row         — BDR-qualified. Label: % converted to Opportunity, count
 *                     that did not progress.
 *   Opportunity row — terminal. No label.
 *
 * Data from /api/marketing/funnel (totals field — all-time):
 *  - MQL:         mql_events stage=MQL
 *  - SAL:         mql_events stage=SAL
 *  - SQL:         mql_events stage=SQL
 *  - Opportunity: mql_events stage=OPPORTUNITY
 *
 * Tenant 1 only.
 */

import { useEffect, useState } from "react";
import { api, getCurrentTenant } from "@/lib/session";

const TENANT_ID = 1;

interface WaterfallStage {
  label: string;
  count: number | null;
  dropOffPct: number | null;
  convertPct: number | null;
}

// Canon palette per CANON_VISUALS_LOCKED.md. Matches the funnel ramp directly
// above this component on the Marketing page.
//   MQL          — gray         (raw upstream volume from marketing)
//   SAL          — yellow       (BDR accepted, in queue)
//   SQL          — orange       (BDR qualified, working)
//   Opportunity  — green        (goal state, handed to AE)
const STAGE_COLORS = ["#6B7280", "#EAB308", "#EA580C", "#16A34A"];

export function ConversionWaterfall() {
  const [stages, setStages] = useState<WaterfallStage[]>([]);
  const [loading, setLoading] = useState(true);
  const tenant = getCurrentTenant();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    api<{
      totals: { MQL: number | null; SAL: number | null; SQL: number | null; OPPORTUNITY: number | null };
    }>("/api/marketing/funnel")
      .then((data) => {
        if (cancelled) return;

        const mql = data?.totals?.MQL ?? 0;
        const sal = data?.totals?.SAL ?? 0;
        const sql = data?.totals?.SQL ?? 0;
        const opp = data?.totals?.OPPORTUNITY ?? 0;

        function convPct(to: number, from: number): number | null {
          if (!from) return null;
          return Math.round((to / from) * 100);
        }
        function dropPct(to: number, from: number): number | null {
          if (!from) return null;
          return Math.round(((from - to) / from) * 100);
        }

        // Each row's metrics describe THIS row's transition forward to the
        // next stage. Opportunity is terminal — both fields null so the row
        // renders no downstream label.
        const stageList: WaterfallStage[] = [
          { label: "MQL",         count: mql, dropOffPct: dropPct(sal, mql), convertPct: convPct(sal, mql) },
          { label: "SAL",         count: sal, dropOffPct: dropPct(sql, sal), convertPct: convPct(sql, sal) },
          { label: "SQL",         count: sql, dropOffPct: dropPct(opp, sql), convertPct: convPct(opp, sql) },
          { label: "Opportunity", count: opp, dropOffPct: null,              convertPct: null },
        ];

        setStages(stageList);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [tenant?.tenantId]);

  if (loading) {
    return (
      <div className="rounded-lg border border-neutral-200 bg-white p-4 animate-pulse">
        <div className="h-4 w-48 bg-neutral-100 rounded mb-4" />
        {[1,2,3,4].map(i => (
          <div key={i} className="h-12 w-full bg-neutral-100 rounded mb-2" style={{ width: `${100 - i * 15}%` }} />
        ))}
      </div>
    );
  }

  const maxCount = Math.max(...stages.map(s => s.count ?? 0), 1);

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-neutral-500">
        MQL → SQL Conversion Waterfall
      </div>
      <p className="mb-5 text-[11px] text-neutral-400">
        Stage volume and drop-off at each transition. Sourced from mql_events stage tracking.
      </p>

      <div className="space-y-3">
        {stages.map((stage, i) => {
          const barPct = maxCount > 0 ? Math.max(8, ((stage.count ?? 0) / maxCount) * 100) : 8;
          const color = STAGE_COLORS[i];
          const nextStage = stages[i + 1];
          // Forward-looking drop count: leads on THIS row that did not advance
          // to the next row. Null on the terminal row (Opportunity).
          const dropCount =
            nextStage != null
              ? Math.max(0, (stage.count ?? 0) - (nextStage.count ?? 0))
              : null;
          const drop = stage.dropOffPct;

          return (
            <div key={stage.label} className="flex items-center gap-4">
              <div className="w-24 shrink-0">
                <div className="text-[11px] font-semibold uppercase tracking-wide" style={{ color }}>
                  {stage.label}
                </div>
                <div className="text-[10px] text-neutral-400 tabular-nums">
                  {stage.count != null ? stage.count.toLocaleString() : "—"}
                </div>
              </div>

              <div className="flex-1 relative">
                <div
                  className="h-8 rounded transition-all duration-500 flex items-center px-3"
                  style={{ width: `${barPct}%`, backgroundColor: color }}
                >
                  <span className="text-sm font-bold text-white tabular-nums">
                    {stage.count != null ? stage.count.toLocaleString() : "—"}
                  </span>
                </div>
              </div>

              <div className="w-40 shrink-0 text-right">
                {stage.convertPct != null && dropCount != null && nextStage != null ? (
                  <span
                    className="text-[11px]"
                    style={{ color: drop != null && drop > 50 ? "#DC2626" : "#6B7280" }}
                  >
                    {stage.convertPct}% convert to {nextStage.label}
                    {" · "}
                    <span className="font-semibold">{dropCount}</span> drop off
                  </span>
                ) : (
                  <span className="text-[11px] text-neutral-300">—</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
